import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  Schedule,
  Sync,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import EscritorioSelect from '../../selects/EscritorioSelect';
import { importService } from '../../../services/importService';

interface ImportSysupdespesasModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedData {
  valid: boolean;
  validationErrors?: string[];
  importStatus?: 'pending' | 'importing' | 'success' | 'error';
  importError?: string;
  [key: string]: any;
}

interface ImportStats {
  total: number;
  valid: number;
  invalid: number;
}

interface ImportProgress {
  nome: string;
  status: 'processando' | 'sucesso' | 'erro';
  erro?: string;
}

const steps = [
  'Configuração da API',
  'Análise e Importação'
];

export const ImportSysupdespesasModal: React.FC<ImportSysupdespesasModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState('https://www.seusite.com.br/admin/addons/sysup/apiv2/listarDespesas');
  const [apiToken, setApiToken] = useState('9f4c8db4c60a93f1a3a77a9dd11b2f5c49c634b9dcf32776f79c4fc343a3b07d');
  const [escritorioId, setEscritorioId] = useState<number | null>(null);
  const [parsedData, setParsedData] = useState<ParsedData[]>([]);
  const [importStats, setImportStats] = useState<ImportStats>({
    total: 0,
    valid: 0,
    invalid: 0
  });
  // Estado de edição removido - interface simplificada
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ [key: number]: ImportProgress }>({});
  const [importCompleted, setImportCompleted] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Funções de seleção de checkbox
  const handleSelectRow = (rowIndex: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === parsedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(parsedData.map((_, index) => index)));
    }
  };

  const getFieldValidationErrors = (row: ParsedData): { [key: string]: boolean } => {
    const fieldErrors: { [key: string]: boolean } = {};

    // Verificar campos obrigatórios
    if (!row.descricao) fieldErrors.descricao = true;
    if (!row.categoria) fieldErrors.categoria = true;
    if (!row.tipo) fieldErrors.tipo = true;
    if (!row.valor) fieldErrors.valor = true;
    if (!row.data_vencimento) fieldErrors.data_vencimento = true;
    if (!row.status_despesa) fieldErrors.status_despesa = true;
    return fieldErrors;
  };

  const parseApiData = (despesasData: any[]): ParsedData[] => {
    try {
      const data: ParsedData[] = [];
      despesasData.forEach((despesa) => {
        const despesaData: ParsedData = {
          descricao: despesa.descricao || '',
          categoria: despesa.categoria || '',
          tipo: despesa.tipo || '',
          valor: despesa.valor || '',
          data_vencimento: despesa.data_vencimento || '',
          data_pagamento: despesa.data_pagamento || '',
          valor_pago: despesa.valor_pago || '',
          status_despesa: despesa.status_despesa || '',
          qtde_parcela: despesa.qtde_parcela || '',
          num_parcela: despesa.num_parcela || '',
          createdAt: despesa.createdAt || '',
          valid: true,
          validationErrors: []
        };

        // Validações básicas
        let isValid = true;
        let validationErrors: string[] = [];

        if (!despesaData.descricao) {
          isValid = false;
          validationErrors.push('Descrição obrigatória');
        }
        if (!despesaData.categoria) {
          isValid = false;
          validationErrors.push('Categoria obrigatória');
        }
        if (!despesaData.tipo) {
          isValid = false;
          validationErrors.push('Tipo obrigatório');
        }
        if (!despesaData.valor) {
          isValid = false;
          validationErrors.push('Valor obrigatório');
        }
        if (!despesaData.data_vencimento) {
          isValid = false;
          validationErrors.push('Data de vencimento obrigatória');
        }
        if (!despesaData.status_despesa) {
          isValid = false;
          validationErrors.push('Status obrigatório');
        }

        despesaData.valid = isValid;
        despesaData.validationErrors = validationErrors;

        data.push(despesaData);
      });

      return data;
    } catch (error) {
      console.error('Erro ao processar dados da API:', error);
      throw new Error('Erro ao processar dados da API');
    }
  };

  const handleApiUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiUrl(event.target.value);
    setError(null);
  };

  const handleApiTokenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiToken(event.target.value);
    setError(null);
  };

  const handleFetchData = async () => {
    if (!apiUrl.trim()) {
      setError('URL da API é obrigatória');
      return;
    }

    if (!apiToken.trim()) {
      setError('Token da API é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fazer requisição para a API externa
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      const apiData = await response.json();

      // Verificar se a resposta contém dados de despesas
      let despesasData: any[];

      if (Array.isArray(apiData)) {
        despesasData = apiData;
      } else if (apiData && typeof apiData === 'object') {
        // Procurar por propriedades que contêm arrays de despesas
        const possibleArrayKeys = Object.keys(apiData).filter(key =>
          Array.isArray(apiData[key])
        );

        if (possibleArrayKeys.length === 0) {
          throw new Error('Nenhum array de clientes encontrado na resposta da API');
        }

        // Usar o primeiro array encontrado (geralmente é 'data', 'despesas', 'despesas', etc.)
        const arrayKey = possibleArrayKeys[0];
        despesasData = apiData[arrayKey];
        console.log(`Array de despesas encontrado na propriedade: ${arrayKey}`);
      } else {
        throw new Error('Formato de resposta inválido da API');
      }

      if (!Array.isArray(despesasData) || despesasData.length === 0) {
        throw new Error('Nenhuma despesa encontrada na resposta da API');
      }

      // Processar dados da API
      const parsedData = parseApiData(despesasData);

      // Ordenar dados: primeiro inválidos, depois válidos
      const sortedData = parsedData.sort((a, b) => {
        // Primeiro critério: inválidos primeiro
        if (!a.valid && b.valid) return -1;
        if (a.valid && !b.valid) return 1;

        // Segundo critério: ordenar por ID (numérico se possível)
        const idA = parseInt(a.id) || 0;
        const idB = parseInt(b.id) || 0;
        return idA - idB;
      });

      setParsedData(sortedData);
      setImportStats({
        total: parsedData.length,
        valid: sortedData.filter(item => item.valid).length,
        invalid: sortedData.filter(item => !item.valid).length
      });
      setSelectedRows(new Set()); // Limpar seleção ao buscar novos dados
      setActiveStep(1);
      toast.success(`Dados obtidos com sucesso! ${sortedData.length} registros encontrados.`);
    } catch (err: any) {
      console.error('Erro ao buscar dados da API:', err);
      setError(err.message || 'Erro ao conectar com a API. Verifique a URL e o token.');
    } finally {
      setLoading(false);
    }
  };

  // Funções de edição removidas - interface simplificada

  const handleStartImport = async () => {
    // Validar escritório
    if (!escritorioId) {
      setError('Selecione um escritório antes de importar');
      toast.error('Selecione um escritório antes de importar');
      return;
    }

    setImporting(true);
    setImportProgress({});
    setImportCompleted(false);

    try {
      // Filtrar apenas os registros SELECIONADOS e válidos
      const selectedIndices = Array.from(selectedRows);
      const selectedData = parsedData.filter((item, index) =>
        selectedIndices.includes(index) && item.valid
      );

      if (selectedData.length === 0) {
        throw new Error('Nenhum despesa selecionado ou válido para importar');
      }

      // Marcar todos os registros selecionados como "processando"
      const initialProgress: { [key: number]: ImportProgress } = {};
      selectedData.forEach(despesas => {
        const originalIndex = parsedData.findIndex(p => p === despesas);
        initialProgress[originalIndex] = {
          nome: despesas.descricao,
          status: 'processando'
        };
      });
      setImportProgress(initialProgress);

      console.log(`🚀 Iniciando importação em lote de ${selectedData.length} despesas selecionados...`);

      // Usar o serviço de importação
      const result = await importService.importDespesas({
        tableData: selectedData, // Apenas os registros SELECIONADOS
        escritorio_id: escritorioId // ID do escritório selecionado
      });
      console.log('📊 Resultado da importação em lote:', result);

      if (result.success) {
        const { summary, results } = result.data;

        // Atualizar status baseado nos resultados individuais
        const finalProgress: { [key: number]: ImportProgress } = {};

        // Processar resultados individuais se disponíveis
        if (results && Array.isArray(results)) {
          results.forEach((despesasResult: any, resultIndex: number) => {
            const despesas = selectedData[resultIndex];
            const originalIndex = parsedData.findIndex(p => p === despesas);

            finalProgress[originalIndex] = {
              nome: despesas.descricao,
              status: despesasResult.status === 'sucesso' ? 'sucesso' : 'erro',
              erro: despesasResult.status === 'erro' ? despesasResult.erro : undefined
            };
          });
        } else {
          // Se não há resultados individuais, usar sumário geral
          selectedData.forEach(despesas => {
            const originalIndex = parsedData.findIndex(p => p === despesas);
            finalProgress[originalIndex] = {
              nome: despesas.descricao,
              status: 'sucesso' // Assumir sucesso se não há erro específico
            };
          });
        }

        setImportProgress(finalProgress);
        setImportCompleted(true);

        // Mostrar resultado final baseado no sumário
        if (summary.success > 0) {
          toast.success(`${summary.success} despesas importadas com sucesso!`);
        }

        if (summary.errors > 0) {
          toast.warning(`${summary.errors} despesas não puderam ser importadas. Verifique os detalhes na tabela.`);
        }

        // Se todos foram importados com sucesso, chamar onSuccess
        if (summary.errors === 0) {
          setTimeout(() => onSuccess(), 2000);
        }

        // Remover registros importados com sucesso da tabela e da seleção
        const successfulImports = results.filter((result: any) => result.status === 'sucesso');
        if (successfulImports.length > 0) {
          // Identificar índices dos registros importados com sucesso
          const successfulIndices = successfulImports.map((result: any, resultIndex: number) => {
            const despesas = selectedData[resultIndex];
            return parsedData.findIndex(p => p === despesas);
          }).filter((index: number) => index !== -1);

          // Remover registros importados com sucesso
          const remainingData = parsedData.filter((_, index: number) => !successfulIndices.includes(index));
          setParsedData(remainingData);

          // Limpar seleção dos registros importados
          const newSelectedRows = new Set(selectedRows);
          successfulIndices.forEach((index: number) => newSelectedRows.delete(index));
          setSelectedRows(newSelectedRows);

          console.log(`🗑️ Removidos ${successfulImports.length} registros importados com sucesso da tabela`);
          toast.info(`${successfulImports.length} registros importados foram removidos da tabela`);
        }

      } else {
        throw new Error(result.message || 'Erro na importação em lote');
      }

    } catch (err: any) {
      console.error('❌ Erro ao importar despesas em lote:', err);

      // Marcar todos os selecionados como erro se a requisição falhou completamente
      const errorProgress: { [key: number]: ImportProgress } = {};
      const selectedIndices = Array.from(selectedRows);
      const selectedData = parsedData.filter((item, index) =>
        selectedIndices.includes(index) && item.valid
      );

      selectedData.forEach(despesas => {
        const originalIndex = parsedData.findIndex(p => p === despesas);
        errorProgress[originalIndex] = {
          nome: despesas.descricao,
          status: 'erro',
          erro: err.message || 'Erro na importação'
        };
      });

      setImportProgress(errorProgress);
      setError(err.message || 'Erro ao importar despesas. Tente novamente.');
      toast.error(err.message || 'Erro ao importar despesas');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setApiUrl('');
    setApiToken('');
    setEscritorioId(null);
    setParsedData([]);
    setError(null);
    setLoading(false);
    // setEditingData removido
    setImporting(false);
    setImportProgress({});
    setImportCompleted(false);
    setSelectedRows(new Set());
    onClose();
  };

  const renderUploadStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Conecte-se à API externa para importar dados de despesas
      </Typography>

      <Card sx={{ mt: 3, p: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <CloudUpload sx={{ fontSize: '4rem', color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Configuração da API
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Informe a URL e token de acesso da API externa
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                label="URL da API"
                placeholder="https://api.exemplo.com/despesas"
                value={apiUrl}
                onChange={handleApiUrlChange}
                fullWidth
                variant="outlined"
                helperText="URL completa da API que retorna dados de despesas"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      <CloudUpload color="action" />
                    </Box>
                  )
                }}
              />
            </Box>

            <TextField
              label="Token de Acesso"
              placeholder="seu_token_de_acesso_aqui"
              value={apiToken}
              onChange={handleApiTokenChange}
              fullWidth
              variant="outlined"
              type="password"
              helperText="Token Bearer para autenticação na API"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Description color="action" />
                  </Box>
                )
              }}
            />

            {apiUrl && apiToken && (
              <Box sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'success.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.main'
              }}>
                <Typography variant="body2" color="success.dark">
                  ✓ Configuração da API preenchida. Clique em "Buscar Dados" para continuar.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderAnalysisStep = () => (
    <Box>


      {/* Seleção de Escritório */}
      <Box sx={{ mb: 3 }}>
        <EscritorioSelect
          value={escritorioId}
          onChange={(value) => {
            setEscritorioId(value as number | null);
            setError(null);
          }}
          label="Escritório"
          required
          helperText="Selecione o escritório onde as despesas serão importadas"
          size="medium"
          error={error === 'Selecione um escritório antes de importar'}
        />
      </Box>

      {/* Barra de progresso durante importação */}
      {importing && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            Importando despesas...
          </Typography>
        </Box>
      )}

      {/* Estatísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {importStats.total}
              </Typography>
              <Typography variant="caption">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {importStats.valid}
              </Typography>
              <Typography variant="caption">
                Válidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {importStats.invalid}
              </Typography>
              <Typography variant="caption">
                Inválidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contador de seleção */}
      {selectedRows.size > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Alert severity="info" sx={{ flex: 1 }}>
            <strong>{selectedRows.size}</strong> {selectedRows.size === 1 ? 'registro selecionado' : 'registros selecionados'}
          </Alert>
          <Button
            onClick={() => setSelectedRows(new Set())}
            size="small"
            sx={{ ml: 2 }}
            variant="outlined"
          >
            Limpar Seleção
          </Button>
        </Box>
      )}

      {/* DataGrid com paginação e checkbox customizado */}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={parsedData.map((row, index) => ({ ...row, rowIndex: index }))}
          getRowId={(row) => row.rowIndex}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          columns={[
            {
              field: 'checkbox',
              headerName: '',
              width: 50,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderHeader: () => (
                <IconButton
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ padding: 0 }}
                >
                  {selectedRows.size === 0 ? (
                    <CheckBoxOutlineBlank fontSize="small" />
                  ) : selectedRows.size === parsedData.length ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <IndeterminateCheckBox fontSize="small" color="primary" />
                  )}
                </IconButton>
              ),
              renderCell: (params) => (
                <IconButton
                  size="small"
                  onClick={() => handleSelectRow(params.row.rowIndex)}
                  sx={{ padding: 0 }}
                >
                  {selectedRows.has(params.row.rowIndex) ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <CheckBoxOutlineBlank fontSize="small" />
                  )}
                </IconButton>
              ),
            },
            {
              field: 'status',
              headerName: 'Status',
              width: 150,
              renderCell: (params) => {
                const row = params.row;
                const index = params.row.rowIndex;
                const progress = importProgress[index];

                // Se está importando ou há progresso, mostrar status de importação
                if (importing || progress) {
                  if (progress?.status === 'processando') {
                    return (
                      <Chip
                        icon={<Sync className="animate-spin" />}
                        label="Importando..."
                        color="info"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else if (progress?.status === 'sucesso') {
                    return (
                      <Chip
                        icon={<CheckCircle />}
                        label="Importado"
                        color="success"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else if (progress?.status === 'erro') {
                    return (
                      <Chip
                        icon={<ErrorIcon />}
                        label="Erro"
                        color="error"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                        title={progress.erro}
                      />
                    );
                  } else if (importing && row.valid) {
                    return (
                      <Chip
                        icon={<Schedule />}
                        label="Aguardando"
                        color="default"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  }
                }

                // Status padrão de validação
                if (row.valid) {
                  return (
                    <Chip
                      icon={<CheckCircle />}
                      label="Válido"
                      color="success"
                      size="small"
                      sx={{ height: '24px', fontSize: '0.75rem' }}
                    />
                  );
                } else {
                  return (
                    <Chip
                      icon={<ErrorIcon />}
                      label="Inválido"
                      color="error"
                      size="small"
                      sx={{ height: '24px', fontSize: '0.75rem' }}
                    />
                  );
                }
              }
            },
            {
              field: 'erro',
              headerName: 'Erro',
              width: 250,
              renderCell: (params) => {
                const index = params.row.rowIndex;
                const progress = importProgress[index];
                const validationErrors = params.row.validationErrors;

                if (progress?.erro) {
                  return (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                      {progress.erro}
                    </Typography>
                  );
                } else if (validationErrors && validationErrors.length > 0) {
                  return (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                      {validationErrors.join(', ')}
                    </Typography>
                  );
                }
                return '-';
              }
            },
            ...(parsedData.length > 0
              ? Object.keys(parsedData[0])
                .filter(key => !['valid', 'validationErrors', 'isEditing', 'rowIndex'].includes(key))
                .map(key => ({
                  field: key,
                  headerName: key.replace(/_/g, ' ').toUpperCase(),
                  width: 150,
                  renderCell: (params: any) => {
                    const row = params.row;
                    const fieldErrors = getFieldValidationErrors(row);
                    const hasError = fieldErrors[key];

                    return (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          border: hasError ? '1px dashed #f44336' : 'none',
                          backgroundColor: hasError ? '#ffebee' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {String(params.value || '')}
                        </Typography>
                      </Box>
                    );
                  }
                }))
              : []
            )
          ]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100, page: 0 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          density="compact"
          sx={{
            '& .MuiDataGrid-cell': {
              padding: '4px 8px',
              fontSize: '0.75rem',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
            },
          }}
        />
      </Box>
    </Box>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderUploadStep();
      case 1:
        return renderAnalysisStep();
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            variant="contained"
            onClick={handleFetchData}
            disabled={!apiUrl || !apiToken || loading}
            startIcon={<CloudUpload />}
          >
            {loading ? 'Buscando Dados...' : 'Buscar Dados'}
          </Button>
        );
      case 1:
        const selectedValidCount = Array.from(selectedRows).filter(index =>
          parsedData[index]?.valid
        ).length;

        return (
          <>
            <Button onClick={() => setActiveStep(0)} disabled={importing}>
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={handleStartImport}
              disabled={selectedValidCount === 0 || importing}
              color="success"
              startIcon={importing ? <Sync className="animate-spin" /> : <CloudUpload />}
            >
              {importing
                ? `Importando ${selectedValidCount} despesas selecionadas...`
                : importCompleted
                  ? 'Importação Concluída'
                  : selectedRows.size === 0
                    ? 'Selecione despesas para importar'
                    : `Importar ${selectedValidCount} despesa${selectedValidCount !== 1 ? 's' : ''} selecionada${selectedValidCount !== 1 ? 's' : ''}`
              }
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Importar Despesas MKAuth
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {getStepContent()}

        {/* CSS para animação */}
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .animate-spin {
              animation: spin 1s linear infinite;
            }
          `}
        </style>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        {getActionButtons()}
      </DialogActions>
    </Dialog>
  );
};

export default ImportSysupdespesasModal;